// Tranche AH pass 1: tenant detail page.
//
// Surfaces:
//   - Identity card (name, status, contact email, default-tenant marker)
//   - Cross-link cards (users, employees, access groups, door permissions)
//   - Side rail: failed outbox jobs scoped to the tenant + recent audit
//   - Safe actions: edit, transfer master (cross-link), door permissions
//   - Dangerous actions: suspend / unsuspend / archive — each requires a
//     reason via <DangerousActionButton>
//
// API surface used (no new routes added in this tranche):
//   GET    /api/v1/tenants/:id            — the tenant row
//   PATCH  /api/v1/tenants/:id            — status mutations
//   GET    /api/v1/admin/outbox/jobs      — outbox side rail
//   GET    /api/v1/audit                  — audit side rail

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  Button,
  DangerousActionButton,
  EntityDetailLayout,
  Icon,
  Spinner,
  type AuditEntry,
} from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import {
  fetchAuditForResource,
} from '../lib/detailFetchers.js';
import { CredentialPills } from '../components/CredentialPills.js';
import { employeeRoleLabel } from '../lib/humanLabels.js';

interface TenantDetail {
  id: string;
  name: string;
  contact_email: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  notes: string | null;
  status: 'active' | 'suspended' | 'archived';
  created_at: string;
}

interface TenantEmployee {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  unifi_user_id: string | null;
  created_at: string;
  credential_types: string[];
  last_access: AccessActivitySummary | null;
}

interface AccessActivitySummary {
  event_id: string;
  type: string;
  severity: string;
  occurred_at: string;
  door_name: string | null;
  unifi_device_id: string | null;
}

const STATUS_TONE: Record<TenantDetail['status'], string> = {
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-tertiary-fixed text-on-tertiary-fixed',
  archived: 'bg-surface-container-high text-on-surface-variant',
};

function employeeStatusTone(status: string) {
  switch (status) {
    case 'active':
      return 'bg-secondary-container text-on-secondary-container';
    case 'invited':
      return 'bg-tertiary-fixed text-on-tertiary-fixed';
    case 'suspended':
      return 'bg-error-container text-on-error-container';
    case 'offboarded':
      return 'bg-surface-container-high text-on-surface-variant';
    default:
      return 'bg-surface-container text-on-surface-variant';
  }
}

function activityLabel(activity: AccessActivitySummary | null) {
  if (!activity) return 'Never accessed';
  const door = activity.door_name ?? 'Unknown door';
  return `${new Date(activity.occurred_at).toLocaleString()} · ${door}`;
}

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [employees, setEmployees] = useState<TenantEmployee[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setTenant(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: TenantDetail }>(`/api/v1/tenants/${id}`, {
        skipTenantScope: true,
      }),
      apiClient.get<{ data: TenantEmployee[] }>(`/api/v1/tenants/${id}/employees`, {
        skipTenantScope: true,
      }),
      fetchAuditForResource('tenant', id, 10, { skipTenantScope: true }),
    ])
      .then(([t, e, a]) => {
        if (cancelled) return;
        setTenant(t.data);
        setEmployees(e.data);
        setAudit(a);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load tenant');
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadTick]);

  // UX-013: the key was minted per call, so a retry inside the same
  // confirmation dialog looked like a new intent to the backend. The dialog
  // owns the key and hands it in.
  async function patchStatus(
    target: TenantDetail['status'],
    reason: string,
    key: string,
  ) {
    if (!tenant) return;
    await apiClient
      .withIdempotencyKey(key)
      // The PATCH route audits regardless; the reason is captured by
      // the dangerous-action UI and supplied here so an operator can
      // explain in the audit trail (audit comparator picks up the
      // status field, the reason flows via the `x-action-reason`
      // header convention used elsewhere in the portal).
      .patch(
        `/api/v1/tenants/${tenant.id}`,
        { status: target },
        { headers: { 'x-action-reason': reason } },
      );
    reload();
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/tenants')}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Tenants
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!tenant) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const badges = (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[tenant.status]}`}
      data-testid="tenant-status-chip"
    >
      {tenant.status}
    </span>
  );

  const safeActions = (
    <>
      <Button variant="secondary" onClick={() => navigate(`/tenants?edit=${encodeURIComponent(tenant.id)}`)}>
        <Icon name="edit" className="text-sm" />
        <span className="ml-1">Edit</span>
      </Button>
      <Link
        to="/admin/users"
        className="inline-flex items-center px-3 h-10 rounded-md bg-surface-container text-on-surface hover:bg-surface-container-high font-medium text-sm"
      >
        <Icon name="admin_panel_settings" className="text-sm mr-1" />
        Transfer master
      </Link>
      <Link
        to="/hardware"
        className="inline-flex items-center px-3 h-10 rounded-md bg-surface-container text-on-surface hover:bg-surface-container-high font-medium text-sm"
      >
        <Icon name="meeting_room" className="text-sm mr-1" />
        Door permissions
      </Link>
    </>
  );

  const dangerousActions = (
    <>
      {tenant.status === 'active' ? (
        <DangerousActionButton
          actionLabel="Suspend…"
          title={`Suspend ${tenant.name}?`}
          description="Suspending blocks portal sign-in, revokes sessions, and pauses queued access grants. Existing door access is unchanged; suspend or delete employees separately to revoke it."
          confirmLabel="Suspend tenant"
          testId="tenant-suspend"
          onConfirm={(reason, intentKey) => patchStatus('suspended', reason, intentKey)}
        />
      ) : null}
      {tenant.status !== 'active' ? (
        <DangerousActionButton
          actionLabel="Restore…"
          title={`Restore ${tenant.name}?`}
          description="Restores portal sign-in for this tenant and resumes queued controller work. Individually suspended or deleted employees are not reactivated."
          confirmLabel="Restore tenant"
          testId="tenant-unsuspend"
          onConfirm={(reason, intentKey) => patchStatus('active', reason, intentKey)}
        />
      ) : null}
      {tenant.status !== 'archived' ? (
        <DangerousActionButton
          actionLabel="Archive…"
          title={`Archive ${tenant.name}?`}
          description="Archival removes the tenant from active lists but retains audit history. Door grants are left for master-admin review."
          confirmLabel="Archive tenant"
          testId="tenant-archive"
          onConfirm={(reason, intentKey) => patchStatus('archived', reason, intentKey)}
        />
      ) : null}
    </>
  );

  return (
    <EntityDetailLayout
      title={tenant.name}
      subtitle={tenant.contact_email ?? 'No contact email on file'}
      meta={`Created ${new Date(tenant.created_at).toLocaleDateString()} · ${tenant.id.slice(0, 8)}`}
      badges={badges}
      backHref="/tenants"
      backLabel="Tenants"
      safeActions={safeActions}
      dangerousActions={dangerousActions}
      audit={
        <AuditFeed
          entries={audit}
          label="Recent activity"
        />
      }
      related={<TenantCrossLinks tenantId={tenant.id} />}
    >
      <div className="space-y-4">
        <TenantHeaderCard tenant={tenant} />
        <TenantEmployeesTable employees={employees} tenantId={tenant.id} />
      </div>
    </EntityDetailLayout>
  );
}

function TenantHeaderCard({ tenant }: { tenant: TenantDetail }) {
  return (
    <section
      aria-label="Identity"
      className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
    >
      <KV k="Name" v={tenant.name} />
      <KV k="Status" v={tenant.status} />
      <KV k="Contact email" v={tenant.contact_email ?? '—'} />
      <KV k="Contact name" v={tenant.contact_name ?? '—'} />
      <KV k="Phone number" v={tenant.contact_phone ?? '—'} />
      <KV k="Created" v={new Date(tenant.created_at).toLocaleString()} />
      <div className="md:col-span-2">
        <KV k="Notes" v={tenant.notes ?? '—'} />
      </div>
    </section>
  );
}

function TenantEmployeesTable({
  employees,
  tenantId,
}: {
  employees: TenantEmployee[];
  tenantId: string;
}) {
  const employeeHref = useCallback(
    (employeeId: string) => `/employees/${employeeId}?tenantId=${encodeURIComponent(tenantId)}`,
    [tenantId],
  );

  return (
    <section
      aria-label="Employees"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-outline-variant flex items-center justify-between">
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">Employees</h2>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{employees.length}</span>
      </div>
      {employees.length === 0 ? (
        <div className="px-4 py-6 text-on-surface-variant">No employees on this tenant.</div>
      ) : (
        <>
          <div className="divide-y divide-outline-variant md:hidden">
            {employees.map((employee) => (
              <div
                key={employee.id}
                className="px-4 py-4 transition-colors hover:bg-surface-container-low"
              >
                {/* UX-025: was a role="link" div with an onClick — no href, so
                    no new tab, no copy link, and the master's ?tenantId scope
                    was invisible to anyone inspecting the link. */}
                <Link
                  to={employeeHref(employee.id)}
                  className="rounded-sm font-body-md text-body-md font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {employee.name}
                </Link>
                <div className="text-sm text-on-surface-variant">{employee.email}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-sm text-on-surface-variant">
                  <span>{employeeRoleLabel(employee.role)}</span>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 font-label-md text-label-md ${employeeStatusTone(
                      employee.status,
                    )}`}
                  >
                    {employee.status}
                  </span>
                </div>
                <div className="mt-2">
                  <CredentialPills types={employee.credential_types ?? []} />
                </div>
                <div className="mt-2 text-sm text-on-surface-variant">
                  {activityLabel(employee.last_access)}
                </div>
              </div>
            ))}
          </div>
          <table className="hidden w-full md:table">
            <thead className="bg-surface-container">
              <tr>
                <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">Name</th>
                <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">Role</th>
                <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">Status</th>
                <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">Credentials</th>
                <th className="text-left px-4 py-2 font-label-md text-label-md uppercase text-on-surface-variant">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {employees.map((employee) => (
                <tr
                  key={employee.id}
                  className="transition-colors hover:bg-surface-container-low"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={employeeHref(employee.id)}
                      className="rounded-sm font-body-md text-body-md font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      {employee.name}
                    </Link>
                    <div className="text-sm text-on-surface-variant">{employee.email}</div>
                  </td>
                  <td className="px-4 py-3 text-on-surface">{employeeRoleLabel(employee.role)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 font-label-md text-label-md ${employeeStatusTone(
                        employee.status,
                      )}`}
                    >
                      {employee.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <CredentialPills types={employee.credential_types ?? []} />
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">
                    {activityLabel(employee.last_access)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function TenantCrossLinks({ tenantId }: { tenantId: string }) {
  // TODO(api-gap): there is no tenant-scoped summary endpoint that
  // returns counts (users / employees / access groups / hardware). We
  // surface deep-links here so the operator can click through; once
  // /tenants/:id is enriched we can render the counts inline.
  const targets: Array<{ label: string; icon: string; href: string }> = [
    { label: 'Managers', icon: 'group', href: `/users?tenant=${tenantId}` },
    { label: 'Employees', icon: 'badge', href: `/employees?tenant=${tenantId}` },
    {
      label: 'Access Zones',
      icon: 'lock_open',
      href: `/access-groups?tenant=${tenantId}`,
    },
    { label: 'Schedules', icon: 'schedule', href: `/schedules?tenant=${tenantId}` },
  ];
  return (
    <section
      aria-label="Related"
      className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-outline-variant">
        <h2 className="font-label-md text-label-md uppercase text-on-surface-variant">Related</h2>
      </div>
      <ul className="divide-y divide-outline-variant">
        {targets.map((t) => (
          <li key={t.label}>
            <Link
              to={t.href}
              className="flex items-center gap-3 px-4 py-3 hover:bg-surface-container-low"
              data-testid={`tenant-related-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon name={t.icon} className="text-on-surface-variant" />
              <span className="font-body-md text-body-md text-on-surface flex-1">{t.label}</span>
              <Icon name="chevron_right" className="text-on-surface-variant" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
